import React, { useMemo, useState, memo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { getOr, includes, size } from 'lodash/fp';
import dayjs from 'dayjs';
import {
  FastField, Field, Formik, Form, FieldArray,
} from 'formik';
import * as Yup from 'yup';
import { NavigationPrompt } from '../../common/components';

import DescriptionField from './DescriptionField';
import FormActions from './FormActions';
import FileField from './FileField';
import NumberField from './NumberField';
import OptionField from './OptionField';
import NavigationDialog from './NavigationDialog';
import PDFEditor from './PDFEditor';
import QuestionField from './QuestionField';
import RectField from './RectField';
import ReservationField from './ReservationField';
import SignatureField from './SignatureField';
import TargetField from './TargetField';
import TitleField from './TitleField';

const Schema = Yup.object().shape({
  number: Yup.object({
    year: Yup.number()
      .min(dayjs().year() - 1)
      .max(dayjs().year() + 1),
    issueNumber: Yup.string(),
  }),
  title: Yup.string()
    .max(30)
    .required(),
  description: Yup.string(),
  file: Yup.mixed()
    .required(),
  targets: Yup.array()
    .of(Yup.object({}))
    .required(),
});

const S = {
  Form: styled(Form)`
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  NumberField: styled(NumberField)`
    margin-bottom: 16px;
  `,
  TitleField: styled(TitleField)`
    margin-bottom: 16px;
  `,
  DescriptionField: styled(DescriptionField)`
    margin-bottom: 16px;
  `,
  FileField: styled(FileField)`
    margin-bottom: ${({ margin }) => margin.bottom}px;
  `,
  TargetField: styled(TargetField)`
    margin-bottom: 16px;
  `,
  ReservationField: styled(ReservationField)`
    margin-bottom: 40px;
  `,
};

const SchoolForm = ({
  draft, groups, targets, redirect, onSave, onSubmit, onChangePdf, onErrorPdf,
}) => {
  const [visible, setVisible] = useState(true);

  const initialValues = useMemo(() => ({
    number: {
      year: dayjs().year(),
      issueNumber: '',
    },
    title: '',
    description: '',
    file: null,
    questions: null,
    signatures: null,
    targets: null,
  }), []);

  const hydratedInitialValues = useMemo(() => ({
    number: getOr({ year: dayjs().year(), issueNumber: '' }, 'number', draft.data),
    title: getOr('', 'title', draft.data),
    description: getOr('', 'description', draft.data),
    file: getOr(null, 'blob', draft.data),
    questions: getOr(null, 'questions', draft.data),
    signatures: getOr(null, 'signatures', draft.data),
    targets: null,
  }), [draft.data]);

  return (
    <Formik
      initialValues={draft.data
        ? hydratedInitialValues
        : initialValues}
      validationSchema={Schema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {({
        dirty, errors, values, setFieldValue,
      }) => (
        <>
          <S.Form>
            <FastField name="number" component={S.NumberField} />
            <FastField name="title" component={S.TitleField} />
            <FastField name="description" component={S.DescriptionField} />
            <FastField name="file" onChangePdf={onChangePdf} component={S.FileField} margin={{ bottom: values.file ? 16 : 72 }} />
            {values.file && (
              <PDFEditor
                file={values.file}
                questions={values.questions}
                signatures={values.signatures}
                setFieldValue={setFieldValue}
                visible={visible}
                setVisible={setVisible}
                onError={onErrorPdf}
              >
                {args => (
                  <>
                    <FieldArray name="questions">
                      {arrayHelpers => <QuestionField name="questions" setVisible={setVisible} {...arrayHelpers} {...args} />}
                    </FieldArray>
                    {args.step === 3 && includes(args.type, ['SINGLE_SELECTION', 'MULTIPLE_SELECTION']) && (
                      <FieldArray name={`questions[${size(values.questions) - 1}].options`}>
                        {arrayHelpers => (
                          <OptionField
                            name={`questions[${size(values.questions) - 1}].options`}
                            setVisible={setVisible}
                            {...arrayHelpers}
                            {...args}
                          />
                        )}
                      </FieldArray>
                    )}
                    {args.step === 3 && args.type === 'DESCRIPTIVE' && (
                      <FastField name={`questions[${size(values.questions) - 1}].field`}>
                        {fieldProps => <RectField setVisible={setVisible} {...fieldProps} {...args} />}
                      </FastField>
                    )}
                    <FieldArray name="signatures">
                      {arrayHelpers => <SignatureField name="signatures" setVisible={setVisible} {...arrayHelpers} {...args} />}
                    </FieldArray>
                  </>
                )}
              </PDFEditor>
            )}
            <Field name="targets">{fieldProps => <S.TargetField {...fieldProps} groups={groups} targets={targets} />}</Field>
            <FormActions errors={errors} values={values} onSave={onSave} />
          </S.Form>
          <NavigationPrompt when={dirty && !redirect}>
            {({ open, navigate, onClose }) => (
              <NavigationDialog open={open} onClose={onClose} onConfirm={navigate} />
            )}
          </NavigationPrompt>
        </>
      )}
    </Formik>
  );
};

SchoolForm.propTypes = {
  draft: PropTypes.shape({
    data: PropTypes.shape({}),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
  groups: PropTypes.shape({
    data: PropTypes.shape({
      clazzGroups: PropTypes.arrayOf(
        PropTypes.shape({
          class_name: PropTypes.string,
          contacts_count: PropTypes.number,
          created_at: PropTypes.string,
          grade: PropTypes.number,
          id: PropTypes.number,
          updated_at: PropTypes.string,
        }),
      ),
      customGroups: PropTypes.arrayOf(
        PropTypes.shape({
          contacts_count: PropTypes.number,
          created_at: PropTypes.string,
          id: PropTypes.number,
          is_unspecified_group: PropTypes.bool,
          name: PropTypes.string,
          updated_at: PropTypes.string,
        }),
      ),
    }),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
  redirect: PropTypes.bool.isRequired,
  targets: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        class_group: PropTypes.shape({
          class_name: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          contacts_count: PropTypes.number,
          created_at: PropTypes.string,
          grade: PropTypes.number,
          id: PropTypes.number,
          updated_at: PropTypes.string,
        }),
        class_name: PropTypes.string,
        created_at: PropTypes.string,
        custom_groups: PropTypes.arrayOf(
          PropTypes.shape({
            contacts_count: PropTypes.number,
            created_at: PropTypes.string,
            id: PropTypes.number,
            is_unspecified_group: PropTypes.bool,
            name: PropTypes.string,
            updated_at: PropTypes.string,
          }),
        ),
        email: PropTypes.string,
        grade: PropTypes.number,
        id: PropTypes.number,
        mobile: PropTypes.string,
        name: PropTypes.string,
        number: PropTypes.number,
        primary_guardian_contact: PropTypes.shape({
          contact_id: PropTypes.number,
          created_at: PropTypes.string,
          email: PropTypes.string,
          id: PropTypes.number,
          level: PropTypes.string,
          mobile: PropTypes.string,
          name: PropTypes.string,
          relationship: PropTypes.string,
          updated_at: PropTypes.string,
          user_id: PropTypes.number,
        }),
      }),
    ),
    loading: PropTypes.bool.isRequired,
    error: PropTypes.shape({}),
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChangePdf: PropTypes.func.isRequired,
  onErrorPdf: PropTypes.func.isRequired,
};

export default memo(SchoolForm);
